import React, { useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatErr } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { TTLogoMark } from "../components/TopBar";

// Left-side imagery — uploaded by user (35.jpg).
const LEFT_IMAGE_URL =
  "https://customer-assets.emergentagent.com/job_client-portal-dev-6/artifacts/9rhsnll7_35.jpg";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) {
    const dest = user.must_change_password
      ? "/change-password"
      : (user.role === "client" && user.client_id
          ? `/clients/${user.client_id}`
          : user.role === "admin" ? "/home" : "/clients");
    return <Navigate to={dest} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await login(email, password);
      const from = location.state?.from?.pathname;
      if (u.must_change_password) navigate("/change-password");
      else if (from) navigate(from);
      else navigate(
        u.role === "client" && u.client_id ? `/clients/${u.client_id}`
        : u.role === "admin" ? "/home"
        : "/clients"
      );
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A]">
      {/* Left — full-bleed image with logo + tagline overlay, contact line at bottom */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-r border-zinc-900" data-testid="login-hero">
        <img src={LEFT_IMAGE_URL} alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/85" />
        <div className="relative z-10 flex flex-col justify-between items-start p-16 w-full anim-fade-in">
          <div className="flex-1 flex flex-col justify-center">
            <TTLogoMark size={260} className="mb-8 anim-scale-in" />
            <p className="anim-fade-up text-zinc-200 text-[22px] xl:text-[26px] font-display tracking-tight leading-tight"
              style={{ animationDelay: "180ms" }}>
              You&rsquo;re in good hands.
            </p>
          </div>
          <div className="anim-fade-up text-zinc-400 text-[13px] leading-relaxed max-w-md"
            style={{ animationDelay: "320ms" }}
            data-testid="login-contact">
            In case of any issues, please contact your Account Manager.
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6 anim-fade-up" data-testid="login-form">
          <div>
            <div className="t-eyebrow mb-2">Sign in</div>
            <h2 className="h-display-1">Welcome back</h2>
            <p className="text-zinc-500 text-[13px] mt-1.5">Enter your credentials to access your dashboard.</p>
          </div>

          <div className="space-y-3.5">
            <div>
              <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Email</Label>
              <Input id="email" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} required
                className="bg-[#111] border-zinc-800 focus:border-zinc-500 mt-1.5 h-10 text-[13px]"
                placeholder="you@thickandthin.media" data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="bg-[#111] border-zinc-800 focus:border-zinc-500 mt-1.5 h-10 text-[13px]" data-testid="login-password" />
            </div>
          </div>

          {err && <div data-testid="login-error" className="text-[12px] text-[#EF4444] flag-red rounded-md p-2.5">{err}</div>}

          <Button type="submit" disabled={busy} data-testid="login-submit"
            className="w-full h-10 bg-white hover:bg-zinc-200 text-black font-medium text-[13px]">
            {busy ? "Signing in…" : "Sign in →"}
          </Button>

          <div className="text-[11px] text-zinc-600 leading-relaxed pt-2 border-t border-zinc-900 lg:hidden">
            In case of any issues, please contact your Account Manager.
          </div>
        </form>
      </div>
    </div>
  );
}
